import { FC, useEffect, useState } from 'react'

import { Layout } from '../../../containers'
import { Title, Text, Card, Button, Line, Loading, Spacer, Modal, FormMessageError, Icon, useModal } from '../../../components/UI'
import { FamilyGroup, FamilyGroupIntro, FamilyGroupTabs, FamilyGroupCardBorderless, FamilyGroupBenefits, AddFamilyMemberButton, FamilyGroupRequests, FamilyGroupsAsMember } from '../../../components'
import { useFamilyGroup, useAuth } from '../../../hooks'
import { familyGroupService } from '../../../services'

const FamilyGroupPage:FC = () => {
  const [activeTab, setActiveTab] = useState('family');
  const [isMinor, setIsMinor] = useState<boolean>(false);
  const familyGroup = useFamilyGroup();
  const auth = useAuth();

  useEffect(() => {
    const userInfo = auth.getInfo();
    
    const calculateIsMinor = (birthDate: string): boolean => {
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return age < 18;
    };

    const isMinorByFlag = userInfo?.is_a_minor || false;
    const isMinorByAge = userInfo?.date_birth ? calculateIsMinor(userInfo.date_birth) : false;
    
    setIsMinor(isMinorByFlag || isMinorByAge);
  }, []);

  const onFamilyChange = () => {
    familyGroup.getFamilies();
    familyGroup.getFamilyGroupsAsMember();
  }

  const changeActiveTab = (tab: string) => {
    setActiveTab(tab)
  }

  const urlParams = new URLSearchParams(window.location.search);
  const tab = urlParams.get('tab');
  const focusedRequestId   = urlParams.get('focusedRequestId');

  useEffect(() => {
    if(tab) {
      changeActiveTab(tab);
    }
  }, []);

  const getFullname = (family: any) => {
    const { first_name, last_name, second_name } = family
    return `${first_name} ${last_name} ${second_name}`
  }

  const modal = useModal()

  const [modalContent, setModalContent] = useState({
    confirmationMessage: '',
    successMessage: '',
    action: () => {},
  });

  const delFamily = async (id: string, isLeaving = false) => {
    const resp = await familyGroupService.delFamilyGroup(id)

    if(resp.status === 'Ok') {
      if(onFamilyChange) onFamilyChange()
      modal.setStatus('SUCCESS')
      modal.open()
    } else {
      modal.setMessage(resp.message)
      modal.setStatus('ERROR')
      modal.open()
    }
  }

  const openConfirmationModal = ({ 
    confirmationMessage, 
    successMessage, 
    action 
  }: any) => {
    setModalContent({
      confirmationMessage,
      successMessage,
      action
    });
    modal.setStatus('');
    modal.open();
  }

  const onDelFamilyModal = (family: any) => {
    openConfirmationModal({
      confirmationMessage: `¿Estás seguro(a) que deseas eliminar a ${getFullname(family.family_patient)} de tu Grupo familiar?`,
      successMessage: `${getFullname(family.family_patient)} ha sido eliminado(a) de tu Grupo familiar`,
      action: () => delFamily(family.id)
    });
  }

  const handleExitGroup = (familyGroup: any) => {
    openConfirmationModal({
      confirmationMessage: `¿Estás seguro(a) que deseas salir del grupo familiar de ${familyGroup.patient_leader.first_name} ${familyGroup.patient_leader.last_name}?`,
      successMessage: `Has salido del grupo familiar de ${familyGroup.patient_leader.first_name} ${familyGroup.patient_leader.last_name}`,
      action: () => delFamily(familyGroup.id, true)
    });
  }

  return (
    <Layout menu titleMain={3}>
      <Title size="20" border className="lg:fz-18">Mi Grupo familiar</Title>

      <Text size="14" className="lg:fz-16 mt-30 lg:mt-40">
        {familyGroup.data &&
          <>
            {familyGroup.data.length > 0 ?
                'Para proteger los derechos de privacidad del paciente, sólo es posible ver el historial médico de hijos(as) menores de 18 años.'
              :
                'En esta sección podrás crear y administrar tu Grupo familiar. Agregar y eliminar integrantes, reservar horas y ver historial médico.'
            }
          </>
        }
      </Text>

      {familyGroup.data && familyGroup.data.length === 0 && <Line fullWidth className="lg:d-none mt-50" />}

      <Spacer size="xl" />
      
      <FamilyGroupTabs 
        tabs={[{ title: 'Mi grupo familiar', setTab: 'family' }, { title: `Mis solicitudes`, setTab: 'requests' }]} 
        activeTab={activeTab} 
        changeActiveTab={changeActiveTab}
      />
      
      <FamilyGroupCardBorderless>
        {activeTab === 'family' &&
          <>
            {!isMinor &&
              <>
                {familyGroup.data ?
                  <>
                    {familyGroup.data.length > 0 ?
                      <FamilyGroup getFullname={getFullname} onDelFamilyModal={onDelFamilyModal} data={familyGroup.data} />
                    :
                      <FamilyGroupBenefits />
                    }
                  </>
                :
                  <Loading align="center" />
                }

                <Spacer size='xxl'/>
                <AddFamilyMemberButton/>
              </>
            }

            {
              familyGroup.familyGroupsAsMember 
              ? familyGroup.familyGroupsAsMember.length > 0 
                ? <FamilyGroupsAsMember 
                    data={familyGroup.familyGroupsAsMember} 
                    onExitGroup={handleExitGroup}
                    isMinor={isMinor}
                  />
                : <Text>{isMinor && 'No eres miembro de ningún grupo familiar.'}</Text>
              : <Loading align="center" />
            }

          </>
        }

        {activeTab === 'requests' &&
          <FamilyGroupRequests onFamilyChange={onFamilyChange}/>
        }

      </FamilyGroupCardBorderless>

      <Modal show={modal.show} maxWidth="345px" onClose={() => modal.close()}>
        {modal.status === '' &&
          <div className="pt-100 pb-70 px-50 text-center">
            <Text weight="semibold" lineHeight="6">
              {modalContent.confirmationMessage}
            </Text>

            <Button onClick={modalContent.action} fullWidth className="mt-40">CONFIRMAR</Button>
          </div>
        }

        {modal.status === 'SUCCESS' &&
          <div className="py-70 px-50 text-center">
            <Icon name="modal-success" />
            <Text weight="semibold" lineHeight="6" className="mt-30">
              {modalContent.successMessage}
            </Text>
          </div>
        }
        {modal.status === 'ERROR' &&
          <FormMessageError
            className="p-50"
            message={modal.message}
            onClick={() => modal.close()}
          />
        }
      </Modal>
    </Layout>
  )
}

export { FamilyGroupPage }